import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent, Button, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '../components/ui';
import { formatCurrency, formatNumber } from '../lib/utils';
import { ArrowLeft, ExternalLink, TrendingUp, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

const getMonthLabel = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
const getMonthRange = (year, month) => ({
  start: new Date(year, month, 1).toISOString().split('T')[0],
  end: new Date(year, month + 1, 0).toISOString().split('T')[0]
});

export default function AdManagerUrls() {
  const { user, isPro, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [urlData, setUrlData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => { if (!isPro && !isAdmin) navigate('/subscribe'); }, [isPro, isAdmin, navigate]);

  const getViewMonth = useCallback(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - monthOffset, 1);
  }, [monthOffset]);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const vm = getViewMonth();
      const { start, end } = getMonthRange(vm.getFullYear(), vm.getMonth());
      
      const { data: urls } = await supabase.from('admanager_urls').select('*').eq('user_id', user.id).gte('date', start).lte('date', end);

      if (urls) {
        const map = {};
        urls.forEach(r => {
          if (!map[r.url]) map[r.url] = { url: r.url, revenue: 0, impressions: 0, clicks: 0, dates: new Set() };
          map[r.url].revenue += Number(r.revenue) || 0;
          map[r.url].impressions += Number(r.impressions) || 0;
          map[r.url].clicks += Number(r.clicks) || 0;
          map[r.url].dates.add(r.date);
        });
        
        const agg = Object.values(map).map(u => ({
          ...u,
          days: u.dates.size,
          ecpm: u.impressions > 0 ? (u.revenue / u.impressions) * 1000 : 0,
          ctr: u.impressions > 0 ? (u.clicks / u.impressions) * 100 : 0,
        })).sort((a, b) => b.revenue - a.revenue);
        
        setUrlData(agg);
      } else {
        setUrlData([]);
      }
    } catch (e) { toast.error('Load failed'); }
    setLoading(false);
  }, [user, getViewMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalRevenue = urlData.reduce((s, u) => s + u.revenue, 0);
  const totalImpressions = urlData.reduce((s, u) => s + u.impressions, 0);
  const avgEcpm = totalImpressions > 0 ? (totalRevenue / totalImpressions) * 1000 : 0;

  const topRevenue = urlData[0] || null;
  const topEcpm = urlData.filter(u => u.impressions >= 100).sort((a, b) => b.ecpm - a.ecpm)[0] || null;
  const monthLabel = getMonthLabel(getViewMonth());

  if (!isPro && !isAdmin) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/ad-manager"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
          <div><h1 className="text-2xl font-bold">URL Performance</h1><p className="text-muted-foreground">Revenue by URL</p></div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setMonthOffset(Math.min(2, monthOffset + 1))} disabled={monthOffset >= 2}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium min-w-[130px] text-center">{monthLabel}</span>
          <Button size="sm" variant="outline" onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))} disabled={monthOffset <= 0}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">URLs</p><p className="text-2xl font-bold">{urlData.length}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">{monthLabel}</p><p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">Impressions</p><p className="text-2xl font-bold">{formatNumber(totalImpressions)}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-muted-foreground">Avg eCPM</p><p className="text-2xl font-bold">${avgEcpm.toFixed(2)}</p></CardContent></Card>
      </div>

      {(topRevenue || topEcpm) && (
        <div className="grid gap-4 md:grid-cols-2">
          {topRevenue && (
            <Card className="border-green-500/50 bg-green-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-8 w-8 text-green-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground">Top Revenue</p>
                    <a 
                      href={topRevenue.url.startsWith('http') ? topRevenue.url : `https://${topRevenue.url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium truncate block hover:text-primary hover:underline"
                      title={topRevenue.url}
                    >
                      {topRevenue.url}
                    </a>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(topRevenue.revenue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {topEcpm && (
            <Card className="border-purple-500/50 bg-purple-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-purple-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-muted-foreground">Highest eCPM (100+ impr)</p>
                    <a 
                      href={topEcpm.url.startsWith('http') ? topEcpm.url : `https://${topEcpm.url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium truncate block hover:text-primary hover:underline"
                      title={topEcpm.url}
                    >
                      {topEcpm.url}
                    </a>
                    <p className="text-lg font-bold text-purple-600">${topEcpm.ecpm.toFixed(2)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Revenue by URL - {monthLabel}</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="flex justify-center py-8"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead className="min-w-[200px]">URL</TableHead><TableHead className="text-right">Days</TableHead><TableHead className="text-right">Impr.</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">eCPM</TableHead><TableHead className="text-right">CTR</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">%</TableHead></TableRow></TableHeader>
                <TableBody>
                  {urlData.length === 0 ? <TableEmpty colSpan={8} message={`No data for ${monthLabel}`} /> : urlData.map((u, i) => {
                    const pct = totalRevenue > 0 ? (u.revenue / totalRevenue) * 100 : 0;
                    const urlHref = u.url.startsWith('http') ? u.url : `https://${u.url}`;
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <a 
                            href={urlHref} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 hover:text-primary group"
                            title={u.url}
                          >
                            <span className="truncate max-w-[180px]">{u.url}</span>
                            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          </a>
                          <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full" style={{ width: `${pct}%` }} /></div>
                        </TableCell>
                        <TableCell className="text-right">{u.days}</TableCell>
                        <TableCell className="text-right">{formatNumber(u.impressions)}</TableCell>
                        <TableCell className="text-right">{formatNumber(u.clicks)}</TableCell>
                        <TableCell className="text-right">${u.ecpm.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{u.ctr.toFixed(2)}%</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{formatCurrency(u.revenue)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{pct.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}